<template>
  <v-dialog
    content-class="mfa-setup-modal"
    :value="visible"
    @keydown.esc="onBack"
    @click:outside="onBack"
    :max-width="500"
    persistent
  >
    <v-card class="popup-wrapper">
      <div class="close-action">
        <span class="close_btn" @click="onBack">
          <span class="material-icons-outlined">
            close
          </span>
        </span>
      </div>

      <div class="modal-content">
        <div class="title">{{ $t('setupMFA.title') }}</div>
        <div class="description">
          {{ $t('setupMFA.description') }}
        </div>

        <div class="steps-wrapper">
          <div class="step step1">{{ $t('setupMFA.step1') }}</div>

          <div class="step step2">{{ $t('setupMFA.step2') }}</div>
          <qrcode v-if="qrcodeStr" :value="qrcodeStr" :options="{ width: 160, margin: 2 }" class="qrcode"></qrcode>

          <div class="step step3">{{ $t('setupMFA.step3') }}</div>

          <div class="mfa-section">
            <otp-input
              :key="otpInputKey"
              input-classes="otp-input"
              separator=""
              :num-inputs="6"
              :should-auto-focus="true"
              :is-input-num="true"
              class="otp-code"
              :class="{ 'incorrect-code': error }"
              @on-change="codeChanged"
              @on-complete="codeCompleted"
            />

            <div v-if="error" class="error-message">
              {{ error }}
            </div>
          </div>
        </div>

        <div class="buttons">
          <v-btn
            small
            depressed
            class="back-button"
            @click="onBack"
          >
            {{ $t('back') }}
          </v-btn>
          <v-btn
            small
            depressed
            class="verify-button"
            @click="handleVerify"
            :disabled="loading || !mfaCode || mfaCode.length !== 6"
          >
            {{ loading ? 'Verifying...' : 'Complete Setup' }}
          </v-btn>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import OtpInput from '@bachdgvn/vue-otp-input';
import VueQrcode from '@chenfengyuan/vue-qrcode';
import disableOuterScrollOnPopupVisible from '@/mixins/disableOuterScrollOnPopupVisible';

export default {
  name: 'MfaSetupModal',
  components: {
    OtpInput,
    qrcode: VueQrcode,
  },
  mixins: [disableOuterScrollOnPopupVisible],
  props: {
    visible: {
      type: Boolean,
      required: true,
    },
    qrcodeStr: {
      type: String,
      default: '',
    },
    loading: {
      type: Boolean,
      default: false,
    },
    error: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      mfaCode: '',
      otpInputKey: 0,
    };
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.mfaCode = '';
        this.$emit('clear-error');
        this.otpInputKey += 1;
        this.$nextTick(() => {
          setTimeout(() => {
            this.clearMfaCode();
          }, 100);
        });
      }
    },
  },
  methods: {
    codeChanged() {
      this.mfaCode = '';
      this.$emit('clear-error');
    },
    codeCompleted(code) {
      this.mfaCode = code;
      if (code && code.length === 6 && !this.loading) {
        this.handleVerify();
      }
    },
    handleVerify() {
      if (this.mfaCode && this.mfaCode.length === 6) {
        this.$emit('verify', this.mfaCode);
      }
    },
    onBack() {
      this.$emit('back');
    },
    clearMfaCode() {
      this.mfaCode = '';
      this.$nextTick(() => {
        const otpComponent = this.$children.find(child => child.$options.name === 'OtpInput');
        if (otpComponent) {
          if (otpComponent.clearInput) {
            otpComponent.clearInput();
          }
          if (otpComponent.handleClear) {
            otpComponent.handleClear();
          }
          if (otpComponent.otp) {
            otpComponent.otp = [];
          }
          otpComponent.$forceUpdate();
        }
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/design-system';

.mfa-setup-modal {
  .popup-wrapper {
    max-width: rem(500);
    padding: $space-8 $space-10;

    .close-action {
      float: right;

      .close_btn {
        float: right;
        margin-top: -$space-4;
        margin-right: -$space-4;

        &:hover {
          cursor: pointer;
          color: var(--brand-primary);
        }
      }
    }

    .modal-content {
      .title {
        color: var(--text-primary);
        font-size: $font-size-base;
        font-weight: 600;
        letter-spacing: 1.12px;
        line-height: $font-size-md;
        text-transform: uppercase;
        padding-bottom: $space-4;
        text-align: center;
      }

      .description {
        font-weight: normal;
        color: var(--text-primary);
        font-size: $font-size-base;
        letter-spacing: 0;
        line-height: $font-size-lg;
        text-align: center;
        margin-bottom: $space-6;
      }

      .steps-wrapper {
        display: flex;
        flex-direction: column;
        align-items: center;
        margin-bottom: $space-5;

        .step {
          position: relative;
          font-size: $font-size-md;
          padding-left: $font-size-lg;
          margin-bottom: $space-5;
          width: 100%;
          text-align: left;

          &::before {
            position: absolute;
            top: 0;
            left: 0;
            content: '';
            font-size: $font-size-md;
            font-weight: bold;
            color: var(--text-secondary);
          }

          &.step1::before {
            content: '1';
          }

          &.step2::before {
            content: '2';
          }

          &.step3::before {
            content: '3';
          }
        }

        .qrcode {
          border: $border-width-thin solid var(--border-primary);
          border-radius: $radius-sm;
          margin: -$space-2 auto $space-5 auto;
        }

        .mfa-section {
          display: flex;
          flex-direction: column;
          align-items: center;
          width: 100%;

          .otp-code {
            justify-content: center;
            margin-bottom: $space-4;
          }

          .error-message {
            color: var(--severity-critical);
            font-size: $font-size-sm;
            line-height: $font-size-base;
            text-align: center;
          }
        }
      }

      .buttons {
        text-align: right;
        display: flex;
        gap: $space-2;
        justify-content: flex-end;

        :deep(.v-btn) {
          border-radius: $radius-full;
        }

        .back-button.back-button {
          box-sizing: border-box;
          height: rem(36);
          width: rem(86);
          border: $border-width-medium solid var(--border-secondary);
          color: var(--text-secondary);
          border-radius: $radius-full;
          background: linear-gradient(135deg, var(--bg-primary) 0%, var(--bg-secondary) 100%);
          box-shadow: $shadow-sm;
          font-size: $font-size-sm;
          font-weight: 600;
          transition: all 0.3s ease;
          text-transform: none;
          letter-spacing: normal;

          :deep(.v-btn__content) {
            color: var(--text-secondary);
          }

          &:hover:not(:disabled) {
            background: linear-gradient(135deg, var(--bg-secondary) 0%, var(--bg-tertiary) 100%);
            box-shadow: $shadow-md;
          }
        }

        .verify-button.verify-button {
          height: rem(36);
          width: fit-content;
          min-width: rem(140);
          border-radius: $radius-full;
          background: linear-gradient(135deg, var(--brand-primary) 0%, #a855f7 100%);
          box-shadow: 0 3px 8px rgba(143, 36, 208, 0.3);
          border: none;
          color: var(--text-inverse);
          font-size: $font-size-sm;
          font-weight: 600;
          letter-spacing: 0.5px;
          line-height: $font-size-md;
          text-align: center;
          text-transform: none;
          transition: all 0.3s ease;

          :deep(.v-btn__content) {
            color: var(--text-inverse);
          }

          &:hover:not(:disabled) {
            box-shadow: 0 4px 12px rgba(143, 36, 208, 0.4);
          }

          &:disabled {
            background: var(--bg-tertiary);
            color: var(--text-muted);
            box-shadow: none;

            :deep(.v-btn__content) {
              color: var(--text-muted);
            }
          }
        }
      }
    }
  }
}
</style>

<style lang="scss">
@import '~@/assets/styles/design-system';

.mfa-setup-modal .otp-input {
  width: rem(40);
  height: rem(40);
  padding: $space-1;
  margin: 0 $space-2;
  font-size: $font-size-xl;
  border-radius: $radius-xs;
  border: $border-width-thin solid var(--border-primary);
  text-align: center;
  outline: none;

  &.error {
    border: $border-width-thin solid var(--severity-critical);
  }

  &:focus {
    border-color: var(--brand-primary);
  }
}

.mfa-setup-modal .otp-input::-webkit-inner-spin-button,
.mfa-setup-modal .otp-input::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.mfa-setup-modal .otp-code div:nth-child(4) .otp-input {
  margin-left: $space-8;
}

.mfa-setup-modal .incorrect-code .otp-input {
  border-color: var(--severity-critical);
}
</style>
